import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Link2, User, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { bioLinksApi } from '@/api/bioLinks';
import { useAppSettings } from '@/contexts/AppSettingsContext';
import TrackingFields, { emptyTracking, trackingPayload } from '@/components/TrackingFields';
import { MapPin } from 'lucide-react';
import { useRegionRequired } from '@/lib/partner';

export default function CreateBio() {
  const regionRequired = useRegionRequired();
  const navigate = useNavigate();
  const { getPagesDomain } = useAppSettings();
  const [creating, setCreating] = useState(false);
  
  // Form state
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [bio, setBio] = useState('');
  const [tracking, setTracking] = useState(emptyTracking);

  const handleSlugChange = (value) => {
    // Only allow lowercase letters, numbers, and hyphens
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlug(sanitized);
  };

  const handleCreate = async () => {
    if (!displayName.trim()) {
      toast.error('Please enter a display name');
      return;
    }
    if (regionRequired && !tracking.region_id) {
      toast.error('Please select a region before creating this bio page.');
      return;
    }

    setCreating(true);
    try {
      const data = await bioLinksApi.create({
        display_name: displayName.trim(),
        slug: slug || undefined,
        bio: bio.trim(),
        tracking: trackingPayload(tracking),
      });
      
      if (data.success) {
        toast.success('Bio page created!');
        navigate(`/bio/${data.data.id}`);
      } else {
        toast.error(data.detail || 'Failed to create bio page');
      }
    } catch (error) {
      console.error('Error creating bio page:', error);
      toast.error('Failed to create bio page');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="create-bio-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/bio')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">Create Bio Page</h1>
          <p className="text-muted-foreground">Create a new link-in-bio page for social media</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Information
              </CardTitle>
              <CardDescription>Basic information for your bio page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="displayName">Display Name *</Label>
                <Input
                  id="displayName"
                  placeholder="e.g., John Smith"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  data-testid="bio-display-name-input"
                />
                <p className="text-xs text-muted-foreground">
                  This will be shown at the top of your bio page
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Custom URL</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                    {getPagesDomain()}/b/
                  </span>
                  <Input
                    id="slug"
                    placeholder="john-smith"
                    value={slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    className="flex-1"
                    data-testid="bio-slug-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Leave empty to auto-generate from display name
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Bio (Optional)</Label>
                <Textarea
                  id="bio"
                  placeholder="A short bio or tagline..."
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  rows={3}
                  data-testid="bio-text-input"
                />
                <p className="text-xs text-muted-foreground">
                  A short description that appears below your name
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Organization linkage */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Organization
              </CardTitle>
              <CardDescription>Who this bio page belongs to</CardDescription>
            </CardHeader>
            <CardContent>
              <TrackingFields value={tracking} onChange={setTracking} />
            </CardContent>
          </Card>

          {/* Create Button */}
          <div className="flex gap-4">
            <Button variant="outline" onClick={() => navigate('/bio')} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !displayName.trim() || (regionRequired && !tracking.region_id)}
              className="flex-1"
              data-testid="create-bio-btn"
            >
              {creating ? 'Creating...' : 'Create Bio Page'}
            </Button>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <h3 className="font-medium">Preview</h3>
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 p-8">
              {/* Mock Phone Frame */}
              <div className="mx-auto w-64 bg-background rounded-3xl shadow-xl overflow-hidden border-4 border-muted">
                <div className="h-6 bg-muted flex items-center justify-center">
                  <div className="w-16 h-1 bg-muted-foreground/20 rounded-full" />
                </div>
                <div className="p-6 text-center min-h-[300px]">
                  {/* Avatar placeholder */}
                  <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-primary">
                      {displayName ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?'}
                    </span>
                  </div>
                  
                  {/* Name */}
                  <h2 className="font-semibold text-lg mb-1">
                    {displayName || 'Your Name'}
                  </h2>
                  
                  {/* Bio */}
                  {bio && (
                    <p className="text-sm text-muted-foreground mb-4">
                      {bio}
                    </p>
                  )}
                  
                  {/* Sample Links */}
                  <div className="space-y-2 mt-6">
                    <div className="p-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
                      Website
                    </div>
                    <div className="p-3 bg-muted rounded-lg text-sm font-medium">
                      Portfolio
                    </div>
                    <div className="p-3 bg-muted rounded-lg text-sm font-medium">
                      Contact
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          <p className="text-xs text-muted-foreground text-center">
            You can customize colors, add links, and upload an avatar after creating the page
          </p>
        </div>
      </div>
    </div>
  );
}
